import { ReactNode, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

/** Slide-in side panel used for every create/edit form. */
export function Drawer({ title, onClose, children, footer }: {
  title: string; onClose: () => void; children: ReactNode; footer?: ReactNode;
}) {
  const bg = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Escape closes only the top-most drawer (drawers can stack, e.g. a user pop-up over a company)
    const k = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const all = document.querySelectorAll(".drawer-bg");
      if (all[all.length - 1] === bg.current) onClose();
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  return createPortal(
    <div ref={bg} className="drawer-bg" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <aside className="drawer" role="dialog" aria-label={title}>
        <header className="drawer-head">
          <h2>{title}</h2>
          <button className="x" onClick={onClose} aria-label="Close">×</button>
        </header>
        <div className="drawer-body">{children}</div>
        {footer && <footer className="drawer-foot">{footer}</footer>}
      </aside>
    </div>, document.body);
}

export function Field({ label, hint, children, wide }: { label: string; hint?: ReactNode; children: ReactNode; wide?: boolean }) {
  return (
    <label className={`afield ${wide ? "wide" : ""}`}>
      <span>{label}</span>
      {children}
      {hint && <small>{hint}</small>}
    </label>
  );
}

const TONE: Record<string, string> = {
  active: "ok", stock: "info", invited: "info", suspended: "warn", disabled: "bad", closed: "bad", retired: "bad",
  internal: "info", client: "plain",
};
export function Pill({ value, label }: { value: string; label?: string }) {
  return <span className={`apill ${TONE[value] ?? "plain"}`}>{label ?? value[0].toUpperCase() + value.slice(1)}</span>;
}

export function Notice({ kind = "ok", children, onClose }: { kind?: "ok" | "bad" | "warn"; children: ReactNode; onClose?: () => void }) {
  return (
    <div className={`anotice ${kind}`}>
      <div>{children}</div>
      {onClose && <button className="x" onClick={onClose} aria-label="Dismiss">×</button>}
    </div>
  );
}

export function Toolbar({ children }: { children: ReactNode }) {
  return <div className="atoolbar">{children}</div>;
}

export const fmtDate = (iso: string | null | undefined) =>
  iso ? new Intl.DateTimeFormat("en-FJ", { timeZone: "Pacific/Fiji", day: "2-digit", month: "short", year: "numeric" }).format(new Date(iso)) : "—";
export const fmtDateTime = (iso: string | null | undefined) =>
  iso ? new Intl.DateTimeFormat("en-FJ", { timeZone: "Pacific/Fiji", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(iso)) : "—";

/** Turn form state into a body, sending "" as null. */
export const clean = <T extends Record<string, unknown>>(o: T) =>
  Object.fromEntries(Object.entries(o).map(([k, v]) => [k, typeof v === "string" ? (v.trim() === "" ? null : v.trim()) : v]));
